import { memo, useEffect, useState, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import Spinner from "../../components/Spinner";
import { useSnackbar } from "../../utils/index.js";
import {
    getOwnerDisplayCodes,
    createOwnerDisplayCode,
    updateOwnerDisplayCode,
    deleteOwnerDisplayCode,
    getOrganizations,
    getOwnersByOrganization,
} from "../../api/index.js";
import Table from "../../components/Table";

const useStyles = makeStyles((theme) => ({
    container: {
        width: "90%",
        margin: "2rem auto",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "1.5rem",
        color: theme.palette.primary.main,
    },
    formRow: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        paddingTop: "0.5rem",
    },
}));

const OwnerDisplayCodes = () => {
    const classes = useStyles();
    const { success, error } = useSnackbar();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [mappings, setMappings] = useState([]);

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Form state
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState("");
    const [owners, setOwners] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState("");
    const [displayCode, setDisplayCode] = useState("");

    const fetchMappings = useCallback(async () => {
        setIsLoading(true);
        try {
            const { success: scs, mappings: data } = await getOwnerDisplayCodes();
            if (scs) setMappings(data || []);
        } catch {
            error("Σφάλμα κατά τη φόρτωση των αντιστοιχίσεων");
        }
        setIsLoading(false);
    }, [error]);

    const fetchOrganizations = useCallback(async () => {
        try {
            const { organizations: orgs } = await getOrganizations();
            setOrganizations(orgs || []);
        } catch {
            error("Σφάλμα κατά τη φόρτωση οργανισμών");
        }
    }, [error]);

    useEffect(() => {
        fetchMappings();
        fetchOrganizations();
    }, [fetchMappings, fetchOrganizations]);

    // When org changes in form, fetch its owners
    useEffect(() => {
        if (!selectedOrg) {
            setOwners([]);
            setSelectedOwner("");
            return;
        }
        (async () => {
            try {
                const { owners: orgOwners } = await getOwnersByOrganization(selectedOrg);
                setOwners(orgOwners || []);
                setSelectedOwner("");
            } catch {
                error("Σφάλμα κατά τη φόρτωση εκτροφών");
            }
        })();
    }, [selectedOrg, error]);

    const openCreateDialog = () => {
        setEditingId(null);
        setSelectedOrg("");
        setSelectedOwner("");
        setDisplayCode("");
        setDialogOpen(true);
    };

    const openEditDialog = (mapping) => {
        setEditingId(mapping._id);
        setDisplayCode(mapping.displayCode);
        // In edit mode we only change the display code, not the owner/org
        setSelectedOrg("");
        setSelectedOwner("");
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditingId(null);
    };

    const handleSubmit = async () => {
        if (!displayCode.trim()) {
            error("Παρακαλώ εισάγετε κωδικό εμφάνισης");
            return;
        }

        setIsLoading(true);
        try {
            if (editingId) {
                // Update existing mapping
                const { success: scs, message } = await updateOwnerDisplayCode(editingId, displayCode.trim());
                if (scs) {
                    success(message);
                    handleClose();
                    await fetchMappings();
                } else {
                    error(message);
                }
            } else {
                // Create new mapping
                if (!selectedOrg || !selectedOwner) {
                    error("Παρακαλώ επιλέξτε οργανισμό και εκτροφή");
                    setIsLoading(false);
                    return;
                }
                const { success: scs, message } = await createOwnerDisplayCode(selectedOwner, selectedOrg, displayCode.trim());
                if (scs) {
                    success(message);
                    handleClose();
                    await fetchMappings();
                } else {
                    error(message);
                }
            }
        } catch {
            error("Σφάλμα κατά την αποθήκευση");
        }
        setIsLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την αντιστοίχιση;")) return;
        setIsLoading(true);
        try {
            const { success: scs, message } = await deleteOwnerDisplayCode(id);
            if (scs) {
                success(message);
                await fetchMappings();
            } else {
                error(message);
            }
        } catch {
            error("Σφάλμα κατά τη διαγραφή");
        }
        setIsLoading(false);
    };

    const columns = [
        {
            Header: <Typography variant="h8">Οργανισμός</Typography>,
            accessor: "organization.name",
            id: "organizationName",
        },
        {
            Header: <Typography variant="h8">Πραγματικός Κωδικός</Typography>,
            id: "realCode",
            accessor: "owner.code",
            Cell: ({ row }) => row._original?.owner?.code || "-",
        },
        {
            Header: <Typography variant="h8">Επώνυμο</Typography>,
            id: "lastName",
            accessor: "owner.lastName",
            Cell: ({ row }) => row._original?.owner?.lastName || "-",
        },
        {
            Header: <Typography variant="h8">Όνομα</Typography>,
            id: "firstName",
            accessor: "owner.firstName",
            Cell: ({ row }) => row._original?.owner?.firstName || "-",
        },
        {
            Header: <Typography variant="h8">Κωδικός Εμφάνισης</Typography>,
            accessor: "displayCode",
        },
        {
            Header: <Typography variant="h8">Ενέργειες</Typography>,
            id: "actions",
            sortable: false,
            filterable: false,
            Cell: ({ row }) => (
                <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(row._original)}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row._original._id)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <>
            <Spinner open={isLoading} />
            <Box className={classes.container}>
                <Box className={classes.header}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Button
                            variant="text"
                            color="primary"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate("/admin-actions")}
                        >
                            Πίσω
                        </Button>
                        <Typography variant="h5">Αντιστοιχίσεις Κωδικών Εκτροφής</Typography>
                    </Box>
                    <Button variant="contained" color="primary" onClick={openCreateDialog}>
                        + Νέα Αντιστοίχιση
                    </Button>
                </Box>

                <Table
                    data={mappings}
                    columns={columns}
                    noDataText="Δεν βρέθηκαν αντιστοιχίσεις"
                    showPageSizeOptions
                />
            </Box>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingId ? "Επεξεργασία Κωδικού Εμφάνισης" : "Νέα Αντιστοίχιση Κωδικού"}
                </DialogTitle>
                <DialogContent>
                    <Box className={classes.formRow}>
                        {!editingId && (
                            <>
                                <FormControl fullWidth>
                                    <InputLabel>Οργανισμός</InputLabel>
                                    <Select
                                        value={selectedOrg}
                                        label="Οργανισμός"
                                        onChange={(e) => setSelectedOrg(e.target.value)}
                                    >
                                        {organizations.map((org) => (
                                            <MenuItem key={org._id} value={org._id}>
                                                {org.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth disabled={!selectedOrg}>
                                    <InputLabel>Εκτροφή</InputLabel>
                                    <Select
                                        value={selectedOwner}
                                        label="Εκτροφή"
                                        onChange={(e) => setSelectedOwner(e.target.value)}
                                    >
                                        {owners.map((owner) => (
                                            <MenuItem key={owner._id} value={owner._id}>
                                                {`${owner.code} - ${owner.lastName} ${owner.firstName}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        <TextField
                            label="Κωδικός Εμφάνισης"
                            value={displayCode}
                            onChange={(e) => setDisplayCode(e.target.value)}
                            fullWidth
                            placeholder="π.χ. 12345678"
                            helperText="Ο κωδικός που θα εμφανίζεται στον χρήστη αντί του πραγματικού κωδικού"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Ακύρωση</Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {editingId ? "Αποθήκευση" : "Δημιουργία"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default memo(OwnerDisplayCodes);
